import * as React from 'react';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loading } from '@redux/actions';
import Breadcrumb from 'telstra-ui/components/breadcrumb/Breadcrumb';
import { SvgIcon } from 'telstra-ui/components/svg/Icon';
import { Button } from 'telstra-ui/components/button/Button';
import { Application } from '../..';
import { ActivityTracking } from '@telstra-connect/billing';
import Routes from '../../route-constants';
import BillingLayoutComponent from "../../components/billing-layout";
import { loadPaymentMethodsList } from '@telstra-connect/billing/methods/load-payment-methods';
import { loadPaymentMethodsInitial } from '@telstra-connect/billing/actions/load-payment-methods';
import { SubscribedState, ILoadPaymentMethods} from './interfaces';
import {checkIfStaffUser} from '@telstra-connect/utils/http/common/headers';
import { ShowPaymentMethodList } from './component/show-payment-method-list';
import {
    getPaymentToken
} from '@telstra-connect/billing/methods/get-payment-token';
import './style.less';
import { disabledDeleteButton } from '@telstra-connect/billing/methods/disabled-delete-button';
import { useNavigate } from 'react-router-dom';
import TextStyle from '@able/react/dist/TextStyle';
import Spacing from '@able/react/dist/Spacing';
import { getSelectedAccountId } from '@utils/common-functions';
import { BvtFeatureFlags, useBvtFlagsChecker } from '@telstra-connect/utils/bvt-feature-flags';

export const ShowAddPaymentMethods = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {isBvtFlagEnabled: isSingleInvoice} = useBvtFlagsChecker({
        bvtFlagName: BvtFeatureFlags.TEDIG7717SingleInvoiceView,
    })
    const { paymentMethods, signInState, b2bUid, paymentToken, isCloseCredit, editPaymentMethod,
        deletePaymentMethod, disabledDeleteIcon }
    = useSelector((state: SubscribedState): ILoadPaymentMethods => ({
        paymentMethods: state.billing.paymentMethods,
        signInState: state,
        b2bUid: state.user?.oauth?.profile?.b2bUid,
        paymentToken: state.billing.paymentToken,
        isCloseCredit: state.billing.closeCreditForm,
        editPaymentMethod: state.billing.editPaymentMethod,
        deletePaymentMethod: state.billing.deletePaymentMethod,
        disabledDeleteIcon: state.billing.disabledDeleteIcon
    }));

    const [paymentMethod, setPaymentData] = useState(paymentMethods?.data?.paymentMethods);
    const isStaffUser = checkIfStaffUser(signInState)
    const selectedAccountId = getSelectedAccountId();

    const navigateToAddNewPaymentMethod = () => {
        Application.tracker.trackLink(ActivityTracking.Components.PaymentMethods,
            ActivityTracking.Page.billingAddNewPaymentMethod);
        const navUrl = Application.routes.getPageSubpath(
            Routes.AddNewPaymentMethods);
        navigate(navUrl)
    }

    const getCidn = () => {
        const cidnId = sessionStorage.getItem('TC:billing:selectedCidn') ? sessionStorage.getItem('TC:billing:selectedCidn') : '';
        let CIDN = JSON.parse(cidnId);
        if (typeof(CIDN) === 'object') {
            CIDN = CIDN.value ? CIDN.value : '';
        }
        return CIDN;
    }
    const cidn = getCidn();
    if (!isStaffUser) {
        useEffect(() => {
            (async () => {
                dispatch(loadPaymentMethodsInitial());
                dispatch(loading(true));
                await dispatch(loadPaymentMethodsList(cidn, selectedAccountId, b2bUid));
                dispatch(loading(false));
                await dispatch(getPaymentToken(selectedAccountId, cidn));
            })();

        }, [dispatch])
    }
    useEffect(() => {
        setPaymentData(paymentMethods?.data?.paymentMethods);
    }, [paymentMethods?.data?.paymentMethods]);

    useEffect(() => {
        if (editPaymentMethod.status === 'Success') {
            Application.tracker.trackLink(ActivityTracking.Components.savedPaymentMethod,
                ActivityTracking.Links.editSuccessfully);
            dispatch(loading(false));
        }
        if (editPaymentMethod.status === 'Failure') {
            dispatch(loading(false));
            dispatch(disabledDeleteButton({typeOfMethods: 'edit'}));
            Application.tracker.trackLink(ActivityTracking.Components.savedPaymentMethod,
                ActivityTracking.Links.editFailure);
        }
    }, [editPaymentMethod.status]);

    useEffect(() => {
        if (deletePaymentMethod.status === 'Success') {
            Application.tracker.trackLink(ActivityTracking.Components.savedPaymentMethod,
                ActivityTracking.Links.deleteSuccessfully);
            dispatch(loading(false));
        }
        if (deletePaymentMethod.status === 'Failure' && Number(deletePaymentMethod.errorCode) === 422) {
            dispatch(loading(false));
            dispatch(disabledDeleteButton({isShowInlineMsg: true, errCode: '422', errorSubcode: deletePaymentMethod.errorSubcode}));
            Application.tracker.trackLink(ActivityTracking.Components.savedPaymentMethod,
                ActivityTracking.Links.deleteFailure);
        }
        if (deletePaymentMethod.status === 'Failure' && Number(deletePaymentMethod.errorCode) !== 422) {
            dispatch(loading(false));
            dispatch(disabledDeleteButton({typeOfMethods: 'delete'}));
            Application.tracker.trackLink(ActivityTracking.Components.savedPaymentMethod,
                ActivityTracking.Links.deleteFailure);
        }
    }, [deletePaymentMethod.status]);
    const breadCrumbItems = [
        {
            active: false, disabled: false, label: 'Billing',
            onClick: () => {
                navigate(!isSingleInvoice ? Application.routes.getPageSubpath(
                    Routes.SelectedInvoiceSummary(selectedAccountId)) :
                    Application.routes.getPageSubpath(Routes.InvoiceSummaryRoute));
            }
        },
        { active: false, disabled: false, label: 'Payment methods',
            onClick: () => {
                navigate(Application.routes.getPageSubpath(
                    Routes.PaymentMethods));
            }
        },
        { active: true, disabled: false, label: 'Saved payment methods' }
    ];
    const infoText = "Your payment methods are only visible to you, not to others in your organisation";
    return (
        <React.Fragment>
            <BillingLayoutComponent>
                <div className="container ui-billing-payment-details-page">
                    <Breadcrumb separator="icon-system-right-arrow"
                        items={breadCrumbItems} collapse={true} />
                    <div className="col-md-6 col-md-offset-3">
                        <div className='header-saved-payment-method'>Saved payment methods</div>
                        <Spacing top="spacing2x"/>
                        <TextStyle alias="TextBodyLong" colour="Default" alignment="Left">{infoText}</TextStyle>
                        <ShowPaymentMethodList
                            paymentMethodList={paymentMethod}
                            paymentToken={paymentToken}
                            isStaffUser={isStaffUser}
                            cidn={getCidn()}
                            accountId={selectedAccountId}
                            isCloseCredit={isCloseCredit}
                            b2bUid={b2bUid}
                            disabledDeleteIcon={disabledDeleteIcon}/>
                        <div className="add-payment-method-button">
                            <div>
                                <SvgIcon name="icon-system-plus2"
                                    className="plus-icon-color"/></div>
                            <Button variant="secondary"
                                state="app"
                                size="small"
                                className="payment-method-btn"
                                onClick={navigateToAddNewPaymentMethod}>
                            Add a payment method</Button>
                        </div>
                    </div>
                </div>
            </BillingLayoutComponent>
        </React.Fragment>
    )
}
export default ShowAddPaymentMethods;
